import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getAge(birthDate: string): number {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Removes empty values from an object before sending to the backend
 * Removes: empty strings, null, undefined
 * Keeps: false, 0, arrays, and objects
 */
export function removeEmptyValues<T extends Record<string, any>>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {};
  
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    
    // Keep values that are not empty
    if (value !== null && value !== undefined && value !== '') {
      // For nested objects, recursively clean them
      if (typeof value === 'object' && !Array.isArray(value) && value instanceof Date === false) {
        const cleanedNested = removeEmptyValues(value);
        // Only include the nested object if it has any properties after cleaning
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key as keyof T] = cleanedNested as T[keyof T];
        }
      } else {
        // Keep all other values (including false, 0, arrays, dates)
        cleaned[key as keyof T] = value;
      }
    }
  });
  
  return cleaned;
}

/**
 * Formats a number as currency (Serbian Dinar - RSD)
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'RSD')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'RSD'): string {
  return new Intl.NumberFormat('sr-RS', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

